import { Marker } from "@vis.gl/react-maplibre";
import { FC, KeyboardEvent } from "react";

import { ICoordinate, MarkerIconType, onActivateKeyDown } from "@ryde/shared";

import { MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT } from "./maplibre-marker.constants";
import { MapLibreStationPinSvg } from "./maplibre-station-pin";

interface IMapLibreStationPinMarkerProps {
  latitude: number;
  longitude: number;
  type: MarkerIconType;
  color: string;
  /** Colour of a drop-off pin's numbered disc, when it differs from the pin's own. */
  badgeColor?: string;
  /** Number shown inside the pin; omitted for icon pins. */
  label?: string;
  onClick?: (event: { originalEvent: MouseEvent }) => void;
  /**
   * Native browser tooltip. Pass `undefined` while a popup is open for this pin, or the browser's own
   * tooltip will sit on top of it.
   */
  title?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /**
   * Names the action this pin performs, and is what makes it a control: given one, the pin becomes
   * focusable and Enter or Space activates it.
   *
   * Deliberately separate from both `onClick` and `title`. An `onClick` may exist only to stop the event
   * reaching the map, which is not an action — a tab stop that does nothing is worse than none. And
   * `title` is suppressed while a popup is open, so a name taken from it would come and go.
   */
  actionLabel?: string;
  /** Lets the pin be picked up and dropped elsewhere — the address picker's way of moving a location. */
  isDraggable?: boolean;
  /** Where it was dropped. Only fires for a draggable pin. */
  onDragEnd?: (coordinate: ICoordinate) => void;
}

/**
 * A station pin placed on the map: the `<Marker>`, the zero-size wrapper the pin hangs off, and the pin
 * itself. Kept as one component because those three have to agree — the pin is tip-anchored, which only
 * works with `anchor="center"` on a 0×0 root, and viewport alignment is what stops map pitch skewing it
 * into an oval.
 *
 * Every MapLibre map that shows stations mounts this, so a pin looks the same on the ride map, the
 * stations and passengers maps, and the little quick-add preview.
 */
export const MapLibreStationPinMarker: FC<IMapLibreStationPinMarkerProps> = ({
  latitude,
  longitude,
  type,
  color,
  badgeColor,
  label,
  onClick,
  title,
  onMouseEnter,
  onMouseLeave,
  actionLabel,
  isDraggable,
  onDragEnd,
}) => {
  const isActionable = Boolean(actionLabel);

  /*
   * Leaflet markers were tabbable and activated with Enter by default; a MapLibre marker is an ordinary
   * div. Rather than call the handler directly — its argument is a mouse event that `@vis.gl/react-maplibre` builds —
   * this synthesises the click on the element, so keyboard and pointer take the exact same path.
   */
  const handleActivate = onActivateKeyDown(
    (event: KeyboardEvent<HTMLDivElement>): void => event.currentTarget.click(),
  );

  return (
    <Marker
      longitude={longitude}
      latitude={latitude}
      anchor="center"
      {...MAPLIBRE_MARKER_VIEWPORT_ALIGNMENT}
      {...(onClick ? { onClick } : {})}
      draggable={isDraggable}
      onDragEnd={
        onDragEnd
          ? event => onDragEnd({ lat: event.lngLat.lat, lng: event.lngLat.lng })
          : undefined
      }
    >
      <div
        className="maplibre-station-marker"
        title={title}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        role={isActionable ? "button" : undefined}
        tabIndex={isActionable ? 0 : undefined}
        aria-label={actionLabel}
        onKeyDown={isActionable ? handleActivate : undefined}
      >
        <MapLibreStationPinSvg
          type={type}
          color={color}
          badgeColor={badgeColor}
          label={label}
        />
      </div>
    </Marker>
  );
};
